import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import Flex, { type FlexProps } from '@commons/containers/Flex';
import { breakpoints } from '@commons/contexts/responsive';
import { Link } from '@commons/i18n/navigation';
import type { DrupalImage as DrupalImageType } from '@commons/types/drupal';

import IconButton from '../IconButton';
import DrupalImage from '../Image/Drupal';
import TopoPatternImage from '../Image/TopoPattern';
import { Tag } from '../Tag';
import Text from '../Text';
import styles from './Tile.module.scss';

/**
 * Next.js Image `sizes` property for the Tile component.
 *
 * Featured tiles are mostly used in a 4-column layout, so we can use the same sizes property.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/image#sizes
 */

const TILE_IMAGE_SIZES = `(max-width: ${breakpoints.lg}px) 100vw, 424px`;

// Type definition for the Tile component props.

type TileProps = FlexProps & {
  title: string;
  href?: string;
  tag?: string;
  image?: DrupalImageType;
  active?: boolean;
  aspectRatio?: 'square' | 'portrait';
  showButton?: boolean;
  className?: string;
};

// Tile root component.

const Tile = (props: TileProps) => {
  const t = useTranslations('commons');

  const {
    href,
    image,
    title,
    tag,
    aspectRatio = 'square',
    active,
    showButton = true,
    className,
    ...rest
  } = props;

  const renderedImage = image ? (
    <DrupalImage {...image} sizes={TILE_IMAGE_SIZES} />
  ) : (
    <TopoPatternImage alt={title} sizes={TILE_IMAGE_SIZES} />
  );

  const renderedContent = (
    <>
      <Flex
        gap="xs"
        width={12}
        align="start"
        theme="black"
        justify="end"
        direction="column"
        backgroundColor="transparent"
        className={clsx(styles.content, 'h-100')}
        {...rest}
      >
        <Flex.Item className={styles.title}>
          {tag && <Tag marginBottom="xs">{tag}</Tag>}
          <Text as="h3" level="h5" transform="uppercase" marginBottom="none">
            {title}
          </Text>
        </Flex.Item>
        {showButton && (
          <IconButton
            type="chevronRight"
            colors={{ icon: 'white', bg: 'grey-9' }}
            activeColors={{ icon: 'black', bg: 'orange-2' }}
            title={t('learnMore')}
            aria-label={t('learnMore')}
            className={styles.button}
          />
        )}
      </Flex>
      {renderedImage}
    </>
  );

  const classes = clsx(
    styles.tile,
    aspectRatio && styles[aspectRatio],
    active && 'active',
    'has-nested-button',
    className
  );

  if (href) {
    return (
      <Link href={href} className={classes}>
        {renderedContent}
      </Link>
    );
  }

  return <div className={classes}>{renderedContent}</div>;
};

export default Tile;
